/**
 * A poll-interval picker: presets plus a custom value, as one small select.
 *
 * Used both per-target (with a "Default" option that clears the override) and globally
 * (where "Default" is not offered, since the global interval is the default). Kept to a
 * native select so it needs no popover machinery and is keyboard- and screen-reader-
 * friendly for free; a custom value the presets do not cover is added as its own option
 * so the current setting always shows.
 */

import { Timer } from 'lucide-react'
import { useId } from 'react'

import { cn } from '../../lib/cn'

const PRESETS = [10, 15, 30, 60, 120, 300, 600]

interface IntervalControlProps {
  /** The current effective interval in seconds. */
  seconds: number
  /** Whether it is a per-target override (shows the option to reset to default). */
  isCustom?: boolean
  /** null = reset to the global default (per-target only); a number sets the interval. */
  onChange: (seconds: number | null) => void
  allowDefault?: boolean
  pending?: boolean
  label?: string
  className?: string
}

function labelFor(seconds: number): string {
  if (seconds % 60 === 0 && seconds >= 60) return `${seconds / 60}m`
  return `${seconds}s`
}

export function IntervalControl({
  seconds,
  isCustom,
  onChange,
  allowDefault = false,
  pending,
  label = 'Every',
  className,
}: IntervalControlProps) {
  const id = useId()
  // Ensure the current value is selectable even if it is not one of the presets.
  const options = PRESETS.includes(seconds) ? PRESETS : [...PRESETS, seconds].sort((a, b) => a - b)

  return (
    <label className={cn('inline-flex items-center gap-1.5 text-[11px]', className)} htmlFor={id}>
      <Timer size={12} aria-hidden className="text-ink-muted" />
      <span className="text-ink-secondary">{label}</span>
      <select
        id={id}
        disabled={pending}
        // "default" is a sentinel distinct from any numeric value.
        value={allowDefault && !isCustom ? 'default' : String(seconds)}
        onChange={(event) => {
          const value = event.target.value
          onChange(value === 'default' ? null : Number(value))
        }}
        className="rounded-md border bg-surface px-1.5 py-1 text-ink disabled:opacity-60"
      >
        {allowDefault ? (
          <option value="default">Default ({labelFor(seconds)})</option>
        ) : null}
        {options.map((value) => (
          <option key={value} value={String(value)}>
            {labelFor(value)}
          </option>
        ))}
      </select>
    </label>
  )
}
